import { useState } from 'react';
import { motion } from 'framer-motion';
import { useLanguage } from '../context/LanguageContext';
import { Phone, Mail, MapPin, Send, CheckCircle, Clock } from 'lucide-react';

export default function Contact() {
  const { t, isArabic } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);

  const contactInfo = [
    {
      icon: Phone,
      titleKey: 'phone',
      value: '0943 999 339',
      href: 'tel:0943999339',
      dir: 'ltr'
    },
    {
      icon: Mail,
      titleKey: 'email',
      value: 'albasma.alhadithaly@gmail.com',
      href: 'mailto:albasma.alhadithaly@gmail.com',
      dir: 'ltr'
    },
    {
      icon: MapPin,
      titleKey: 'address',
      value: isArabic ? 'ليبيا - طرابلس - طريق الشوك' : 'Libya - Tripoli - Tariq Al-Shawk',
      href: null,
      dir: isArabic ? 'rtl' : 'ltr'
    },
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    const mailtoLink = `mailto:albasma.alhadithaly@gmail.com?subject=${encodeURIComponent(
      isArabic ? 'رسالة من موقع البصمة الحديثة لاستيراد المعدات الطبية' : 'Message from ALBASMA ALHADITHA Website'
    )}&body=${encodeURIComponent(
      `${isArabic ? 'الاسم' : 'Name'}: ${formData.name}\n` +
      `${isArabic ? 'الهاتف' : 'Phone'}: ${formData.phone}\n` +
      `${isArabic ? 'البريد الإلكتروني' : 'Email'}: ${formData.email}\n\n` +
      `${isArabic ? 'الرسالة' : 'Message'}:\n${formData.message}`
    )}`;
    window.location.href = mailtoLink;
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 3000);
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-20 md:py-28 overflow-hidden" data-testid="contact-hero">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-slate-100 to-white" />
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `linear-gradient(to right, #cbd5e1 1px, transparent 1px), linear-gradient(to bottom, #cbd5e1 1px, transparent 1px)`,
              backgroundSize: '60px 60px'
            }}
          />
        </div>

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <span className="inline-block px-4 py-2 bg-black text-white text-xs font-medium rounded-full mb-6 tracking-wider uppercase">
              {isArabic ? 'تواصل معنا' : 'Contact Us'}
            </span>
            <h1 className={`text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {t('contactTitle')}
            </h1>
            <p className="text-lg md:text-xl text-slate-600">
              {t('contactSubtitle')}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section className="py-16 md:py-24 bg-white" data-testid="contact-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: isArabic ? 30 : -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <span className="text-xs uppercase tracking-widest text-slate-500 mb-4 block">
                {isArabic ? 'معلومات التواصل' : 'Contact Information'}
              </span>
              <h2 className={`text-3xl md:text-4xl font-bold text-slate-900 mb-8 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                {isArabic ? 'نحن هنا لمساعدتك' : "We're Here to Help"}
              </h2>

              <div className="space-y-6 mb-10">
                {contactInfo.map((info, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 10 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-4"
                  >
                    <div className="w-12 h-12 rounded-xl bg-black flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <span className="text-sm text-slate-500 block mb-1">{t(info.titleKey)}</span>
                      {info.href ? (
                        <a
                          href={info.href}
                          className="text-slate-900 font-medium hover:text-slate-600 transition-colors"
                          dir={info.dir}
                        >
                          {info.value}
                        </a>
                      ) : (
                        <span className="text-slate-900 font-medium" dir={info.dir}>
                          {info.value}
                        </span>
                      )}
                    </div>
                  </motion.div>
                ))}
              </div>

              {/* Free Delivery Badge */}
              <div className="bg-slate-50 rounded-2xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <Clock className="w-5 h-5 text-black" />
                  <span className={`font-semibold text-slate-900 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                    {isArabic ? 'ساعات العمل' : 'Working Hours'}
                  </span>
                </div>
                <p className="text-slate-600 text-sm">
                  {isArabic 
                    ? 'السبت - الخميس: 9:00 ص - 6:00 م'
                    : 'Saturday - Thursday: 9:00 AM - 6:00 PM'
                  }
                </p>
                <div className="mt-4 pt-4 border-t border-slate-200">
                  <span className="inline-block px-4 py-2 bg-black text-white text-sm rounded-full">
                    {t('freeDelivery')}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: isArabic ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="bg-slate-50 rounded-2xl p-8" data-testid="contact-form">
                <h3 className={`text-xl font-semibold text-slate-900 mb-6 ${isArabic ? 'font-arabic' : 'font-english'}`}>
                  {isArabic ? 'أرسل لنا رسالة' : 'Send us a Message'}
                </h3>
                
                <div className="space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('name')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-black focus:ring-0 transition-colors"
                      data-testid="contact-name-input"
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('phoneNumber')} *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-black focus:ring-0 transition-colors"
                        dir="ltr"
                        data-testid="contact-phone-input"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        {t('emailAddress')}
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-black focus:ring-0 transition-colors"
                        dir="ltr"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('message')} *
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 rounded-xl border border-slate-200 bg-white focus:border-black focus:ring-0 transition-colors resize-none"
                      data-testid="contact-message-input"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full btn-primary flex items-center justify-center gap-2"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitted ? (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        {isArabic ? 'تم الإرسال!' : 'Sent!'}
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        {t('send')}
                      </>
                    )}
                  </button>
                </div>
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Map Section - Placeholder */}
      <section className="py-16 md:py-20 bg-slate-50" data-testid="map-section">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="bg-black rounded-3xl p-8 md:p-12 text-center"
          >
            <MapPin className="w-12 h-12 text-white mx-auto mb-6" />
            <h3 className={`text-2xl md:text-3xl font-bold text-white mb-4 ${isArabic ? 'font-arabic' : 'font-english'}`}>
              {isArabic ? 'موقعنا' : 'Our Location'}
            </h3>
            <p className="text-slate-400 text-lg mb-2">
              {isArabic ? 'ليبيا - طرابلس - طريق الشوك' : 'Libya - Tripoli - Tariq Al-Shawk'}
            </p>
            <p className="text-slate-500">
              {isArabic ? 'توصيل مجاني لجميع مناطق طرابلس' : 'Free delivery to all areas in Tripoli'}
            </p>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
